import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEditLesson } from "hooks/useEditLesson";
import { useCreateGameLessonVersion } from "hooks/useCreateGameLessonVersion";
import { useUpdateLessonImage } from "hooks/useUpdateLessonImage";
import { LessonForm } from "components/views/editor/models/LessonForm";
import { GameEditorData } from "components/views/editor/gameEditor/models/GameEditorData";
import { LESSON_WITH_VERSIONS_QUERY_KEY } from "hooks/useGetLessonWithLatestVersions";

interface UpdateGameLessonParams {
    lessonId: string;
    lessonFormData: LessonForm;
    currentLanguage: string;
    gameEditorData: GameEditorData;
}

export const useUpdateGameLesson = () => {
    const queryClient = useQueryClient();
    const { editLesson } = useEditLesson();
    const { createGameLessonVersion } = useCreateGameLessonVersion();
    const { updateLessonImageFromFile } = useUpdateLessonImage();

    const mutation = useMutation({
        mutationFn: async ({
            lessonId,
            lessonFormData,
            currentLanguage,
            gameEditorData
        }: UpdateGameLessonParams): Promise<boolean> => {
            const editLessonResponse = await editLesson({
                lessonId,
                lessonName: lessonFormData.lessonName,
                tagA: lessonFormData.tagA,
                tagB: lessonFormData.tagB
            });
            if (!editLessonResponse?.success) return false;

            const createVersionResponse = await createGameLessonVersion({
                lessonId,
                language: currentLanguage,
                name: lessonFormData.lessonVersionName,
                description: lessonFormData.description,
                maxPoints: lessonFormData.maxPoints,
                fileId: gameEditorData.fileId!,
                events: gameEditorData.events
                    .filter((e) => e.event.trim() !== "")
                    .map((e) => ({
                        eventKey: e.event,
                        points: Math.max(0, Math.min(lessonFormData.maxPoints, e.points))
                    }))
            });
            if (!createVersionResponse?.success) return false;

            if (lessonFormData.image) {
                const imageUpdateSuccess = await updateLessonImageFromFile(
                    lessonId,
                    lessonFormData.image.file
                );
                if (!imageUpdateSuccess) return false;
            }
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: LESSON_WITH_VERSIONS_QUERY_KEY });
        }
    });

    const updateGameLesson = async (
        lessonId: string,
        lessonFormData: LessonForm,
        currentLanguage: string,
        gameEditorData: GameEditorData
    ): Promise<boolean> => {
        const result = await mutation.mutateAsync({
            lessonId,
            lessonFormData,
            currentLanguage,
            gameEditorData
        });
        return result;
    };

    return { updateGameLesson, isUpdating: mutation.isPending };
};
